<script setup lang="ts">
// DESIGN.md section 9.1, screen 3 — Live match. Data comes from the SSE table stream (single
// source of truth, section 9.2): taps call REST and wait for the echo, never mutate local state
// optimistically. We keep a `snapshot` ref so the screen still has something to render for the
// ~6s "switch ends" banner and for the Result overlay even after the table state moves on
// (liveMatch may go null the instant a match stops being live).
//
// 2026-08-31 round-3 feedback (items 4, 9-12) reworked this screen's layout — see git history /
// DESIGN.md for the full item list. Superseded by round-5 below in several places.
//
// 2026-08-31 round-5 feedback (items 14-18) reworked it again:
//  - item 14: the decorative TableDrawing table is GONE — the user has both it and the player
//    cards on screen and only wants the cards. Its swap-sides pill went with it; that capability
//    moved again in round-9 (2026-08-31): swap-sides now lives in the Settings panel.
//  - item 15: the score bug moves BELOW the cards (was above the table) and switches from numeric
//    games-won to DOTS — `gamesToWin` (Bo{{N}} floor(N/2)+1) dots per side, filled as each side
//    wins a game. Still subtle/score-bug-styled, still shows "BoN".
//  - item 16: cards + score bug + stats are wrapped in one flex-1 column, vertically centered in
//    whatever room is left below the top bar / Undo+swap row, instead of stacking from the top.
//  - item 17: the big red Abandon button left the body; round-12 moved it again, into the
//    Settings panel (SettingsPanel.vue owns the confirm + abandonMatch call now).
//  - item 18: extra top padding above the avatar inside each player card (py-3 → pt-5 pb-3) so it
//    isn't flush against the card's top edge.
// This is noticeably taller than the old fixed 960×540 layout, so the screen still scrolls
// vertically (touch-drag) if content overflows — flagged as unverified on the real kiosk in
// DESIGN.md section 14.
//
// 2026-08-31 round-7 feedback (items 19-20):
//  - item 19: the below-cards stats/history block (matches played, win rates, recent results —
//    round-3 item 12) is REMOVED from this screen entirely; only the subtle match-type-name
//    footer survives. That data's real home is the /players profile (round-4). No backend
//    endpoint was touched — Versus.vue/Player.vue still call getVersus/getHeadToHead.
//  - item 20: KomboLogo.vue now shows an always-on Madrid-time clock in its right gutter, ahead
//    of any `#right` slot content — so this screen reads [clock][Abandon X] in its top bar,
//    with no change needed here beyond KomboLogo.vue itself (see its own file header).
//
// 2026-08-31 round-8 feedback item 22: whole-card "tap the side to score" plus the standalone
// visible "Undo (N)" button are GONE. Each card now has explicit + / − buttons flanking the
// score. + is the old onTapScore(side) unchanged. − reuses the SAME undoPoint() call as before
// (single most-recent-point undo, provenance untouched) but is only ENABLED when that undo would
// actually affect THIS side — i.e. `lastPoint.side === side` — per the coordinator's explicit
// "do NOT bypass event provenance" instruction: there is still only one undo operation (undo the
// last point), we're just gating which side's button may trigger it instead of inventing a
// per-side decrement. The capability doesn't shrink; the always-visible "Undo (N)" affordance
// does, since which side's − is live now IS the provenance indicator.
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { mdiMinus, mdiPlus } from '@mdi/js'
import Avatar from '../components/Avatar.vue'
import KomboLogo from '../components/KomboLogo.vue'
import ServeIndicator from '../components/ServeIndicator.vue'
import TableSide from '../components/TableSide.vue'
import { tableStore } from '../store/table'
import { settings } from '../store/settings'
import { postPoint, undoPoint, getMatch, getTournament, createMatch, finalizeGameFast, getVoiceProfiles, ApiRequestError } from '../backend'
import { onSttCommand, spkForCommand, type SttCommandEvent } from '../stt'
import { interpretScore, rankSpeakerCandidates, classifyConfidenceBand, type ConfidenceBandResult } from '../voiceScore'
import { matchScope, scopedCandidates, scopeLabel } from '../services/candidateScope'
import { startConfirmation } from '../services/confirmFlow'
import type { CornerClaimResult } from '../services/cornerClaim'
import { shipEvent } from '../logging'
import { navigate } from '../store/nav'
import { nextUp, setNextUp, clearNextUp, nextUpReady } from '../store/nextUp'
import { sounds } from '../sounds'
import { t, tFastConfirmBody, tFastInvalidScore } from '../i18n'
import ConfirmDialog from '../components/ConfirmDialog.vue'
import type { Match, Side, TournamentBracketSlot, TournamentDetail, VoiceProfile } from '../types'

const snapshot = ref<Match | null>(null)
const showBanner = ref(false)
const showResult = ref(false)
const posting = ref(false)
const actionError = ref<string | null>(null)

// Round-13: the in-card head-to-head line was removed (feedback: "just keep the elo").
// Round-7 feedback item 19: the below-cards stats/history block (matches played, win rates,
// recent results) was REMOVED from this screen — that data's proper home is the /players
// profile (round-4's Player.vue). The `/rankings/vs` fetch that fed it went with it; the
// GET /api/rankings/vs ENDPOINT itself is untouched (Versus.vue and Player.vue still use it via
// getVersus/getHeadToHead) — only this screen's local use of it was deleted.
let bannerTimer: ReturnType<typeof setTimeout> | null = null
let resultTimer: ReturnType<typeof setTimeout> | null = null
let actionErrorTimer: ReturnType<typeof setTimeout> | null = null

function flashActionError(message: string) {
  actionError.value = message
  if (actionErrorTimer) clearTimeout(actionErrorTimer)
  actionErrorTimer = setTimeout(() => (actionError.value = null), 4000)
}

function returnHome() {
  showResult.value = false
  clearNextUp()
  if (resultTimer) clearTimeout(resultTimer)
  navigate('home')
}

function handleFinalMatch(m: Match) {
  snapshot.value = m
  if (m.status === 'finished') {
    showResult.value = true
    if (resultTimer) clearTimeout(resultTimer)
    resultTimer = setTimeout(returnHome, 20000)
    // Round-13: tournament match -> offer the next pairing on the overlay (cancels the timer
    // if there is one to offer).
    void maybeOfferNextUp()
  } else {
    // abandoned — no result overlay, straight home.
    navigate('home')
  }
}

const stopWatch = watch(
  () => tableStore.state?.liveMatch ?? null,
  (m, prevM) => {
    if (showResult.value) return // overlay is already up; let its own timer/tap drive navigation
    if (m) {
      snapshot.value = m
      if (m.status !== 'live') {
        handleFinalMatch(m)
        return
      }
      if (m.banner === 'switch_ends') {
        showBanner.value = true
        if (bannerTimer) clearTimeout(bannerTimer)
        bannerTimer = setTimeout(() => (showBanner.value = false), 6000)
      }
    } else if (prevM && prevM.status === 'live') {
      // liveMatch vanished without ever carrying a non-live status — fetch the final record
      // directly so the Result overlay still gets its rating changes.
      getMatch(prevM.id)
        .then(handleFinalMatch)
        .catch(() => navigate('home'))
    }
  },
  { immediate: true },
)

// Head-to-head/versus fetch + sound triggers both react to the SAME snapshot changes, but are
// kept separate from the watcher above: that one owns navigation/overlay/banner timing and
// already has several early-return branches, and neither of these concerns should have to reason
// about them. Sound priority per tick (never more than one of match/game/undo fires together — a
// point that also happens to end a game only plays the bigger sound):
//   match just finished > points undone > game just won > point posted; independently, a
//   serve-side change plays its own chirp UNLESS a game/match was just won (that fanfare is
//   already plenty of feedback for "a new game/match started"). Ends-switching is its OWN
// independent trigger (2026-08-31, distinct from serve rotation — see sounds.ts): it fires
// whenever `banner` transitions into 'switch_ends' (between games, or at 5 in the decider),
// staggered slightly after the gameWin fanfare when the two coincide (end of a non-final game)
// so the two chimes read as two separate cues instead of overlapping into mush.
function gamesWonTotal(m: Match): number {
  return Object.values(m.gamesWon).reduce((sum, n) => sum + n, 0)
}

watch(
  snapshot,
  (newM, oldM) => {
    // `immediate: true` so a reload landing directly on an in-progress match still loads its
    // head-to-head/versus record right away, instead of waiting for the next point. On that very
    // first call `oldM` is undefined, so the early return below correctly skips sound-triggering
    // — mounting on a live match must never play a sound for state that already existed.
    if (newM) {
      void loadTournamentContext(newM)
    }
    if (!newM || !oldM || newM.id !== oldM.id) return

    const matchJustFinished = newM.status === 'finished' && oldM.status !== 'finished'
    const pointsDecreased = newM.pointsCount < oldM.pointsCount
    const pointsIncreased = newM.pointsCount > oldM.pointsCount
    const gameJustWon = !matchJustFinished && gamesWonTotal(newM) > gamesWonTotal(oldM)
    const serverChanged = !!newM.server && !!oldM.server && newM.server !== oldM.server
    const endsJustSwitched = !matchJustFinished && newM.banner === 'switch_ends' && oldM.banner !== 'switch_ends'

    if (matchJustFinished) {
      sounds.matchWin()
    } else if (pointsDecreased) {
      sounds.undo()
    } else {
      if (gameJustWon) sounds.gameWin()
      else if (pointsIncreased) sounds.point()
      if (serverChanged && !gameJustWon) sounds.serveChange()
    }

    if (endsJustSwitched) {
      if (gameJustWon) setTimeout(() => sounds.endsSwitch(), 420)
      else sounds.endsSwitch()
    }
  },
  { immediate: true },
)

// Safety net: landed on Live (deep link / stale back-button state) with confirmed no live match.
const confirmedNoLiveMatch = computed(() => !!tableStore.state && !tableStore.state.liveMatch && !snapshot.value && !showResult.value)
watch(confirmedNoLiveMatch, (stuck) => {
  if (stuck) navigate('home')
})

onUnmounted(() => {
  stopWatch()
  unsubVoice?.()
  if (voicePopupTimer) clearTimeout(voicePopupTimer)
  if (bannerTimer) clearTimeout(bannerTimer)
  if (resultTimer) clearTimeout(resultTimer)
  if (actionErrorTimer) clearTimeout(actionErrorTimer)
})

const leftGamesWon = computed(() => (snapshot.value ? (snapshot.value.gamesWon[snapshot.value.currentPlayers.left.id] ?? 0) : 0))
const rightGamesWon = computed(() => (snapshot.value ? (snapshot.value.gamesWon[snapshot.value.currentPlayers.right.id] ?? 0) : 0))
const currentGame = computed(() => (snapshot.value?.currentGameIndex != null ? snapshot.value.games[snapshot.value.currentGameIndex] : null))

// Round-5 feedback item 15: games needed to WIN the match (Bo3 → 2, Bo5 → 3, ...) — re-added so
// the score bug can render that many dots per side instead of a raw games-won number.
const gamesToWin = computed(() => (snapshot.value ? Math.floor(snapshot.value.bestOf / 2) + 1 : 0))

async function onTapScore(side: Side) {
  if (!snapshot.value || snapshot.value.status !== 'live' || posting.value) return
  posting.value = true
  try {
    await postPoint(snapshot.value.id, side, 'touch')
  } catch (e) {
    flashActionError(e instanceof ApiRequestError ? e.message : 'Could not record the point')
  } finally {
    posting.value = false
  }
}

async function onUndo() {
  if (!snapshot.value || snapshot.value.status !== 'live' || snapshot.value.pointsCount === 0 || posting.value) return
  posting.value = true
  try {
    await undoPoint(snapshot.value.id)
  } catch (e) {
    flashActionError(e instanceof ApiRequestError ? e.message : 'Could not undo')
  } finally {
    posting.value = false
  }
}


// Round-8 feedback item 22 — gates a side's "−" button: only the side that scored the MOST
// RECENT point can undo it (undoPoint() only ever undoes the single last point). Reusing
// lastPoint.side keeps this a pure read of existing provenance data, no new backend call.
function canMinus(side: Side): boolean {
  return (
    !!snapshot.value &&
    snapshot.value.status === 'live' &&
    snapshot.value.pointsCount > 0 &&
    snapshot.value.lastPoint?.side === side &&
    !posting.value
  )
}

// ---------------------------------------------------------------------------------------------
// Round-31 (2026-08-31, DESIGN.md §23) — Fast mode: instead of the +/- taps above, each side
// gets a 1..pointsToWin button grid (5 per row) to pick the game's FINAL score directly. Once
// both sides have a pick, a ConfirmDialog shows the exact result before anything is sent — a
// mistap on the wrong number must not silently end a game/match (rider requirement,
// 2026-08-31). Cancelling just dismisses the dialog; the picks stay put so the player can
// correct one side (tapping ANY number, same value or different, clears `fastDismissed` and
// re-shows the dialog — no dead end). Picks are pure client-side UI state, reset whenever the
// game index changes (a new game started, or the match finished) via the watcher below — the
// backend is the only place a game is actually finalized (finalizeGameFast).
const fastLeftPick = ref<number | null>(null)
const fastRightPick = ref<number | null>(null)
const fastDismissed = ref(false)
const fastFinalizing = ref(false)

watch(
  () => (snapshot.value ? `${snapshot.value.id}:${snapshot.value.currentGameIndex}` : null),
  () => {
    fastLeftPick.value = null
    fastRightPick.value = null
    fastDismissed.value = false
  },
)

const fastNumbers = computed<number[]>(() => {
  const n = snapshot.value?.matchType.pointsToWin ?? 0
  return Array.from({ length: n }, (_, i) => i + 1)
})

// Mirrors match_service._validate_fast_score server-side (app/services/match_service.py) —
// this is only a fast, optimistic client-side check; the server re-validates regardless.
function isValidFastScore(a: number, b: number): boolean {
  const m = snapshot.value
  if (!m || a === b) return false
  const hi = Math.max(a, b)
  const lo = Math.min(a, b)
  const threshold = m.matchType.pointsToWin - m.matchType.winBy
  return lo <= threshold ? hi === m.matchType.pointsToWin : hi - lo === m.matchType.winBy
}

const fastBothPicked = computed(() => fastLeftPick.value != null && fastRightPick.value != null)
const fastValid = computed(() => fastBothPicked.value && isValidFastScore(fastLeftPick.value as number, fastRightPick.value as number))
// Chose an inline error over disabling invalid button combinations (DESIGN.md §23) — simpler
// and always correct; disabling would need to reactively recompute, per tap, which numbers on
// the OTHER side are still legal, for marginal benefit over just telling the room why.
const fastInvalidMessage = computed(() => {
  const m = snapshot.value
  if (!m || !fastBothPicked.value || fastValid.value) return null
  return tFastInvalidScore(m.matchType.pointsToWin, m.matchType.winBy)
})
const fastConfirmOpen = computed(() => fastValid.value && !fastDismissed.value && !fastFinalizing.value)
const fastConfirmBody = computed(() => {
  const m = snapshot.value
  if (!m || fastLeftPick.value == null || fastRightPick.value == null) return ''
  return tFastConfirmBody(m.currentPlayers.left.displayName, fastLeftPick.value, m.currentPlayers.right.displayName, fastRightPick.value)
})

function selectFastPick(side: Side, n: number) {
  if (!snapshot.value || snapshot.value.status !== 'live' || fastFinalizing.value) return
  if (side === 'left') fastLeftPick.value = n
  else fastRightPick.value = n
  fastDismissed.value = false // re-selecting (even the same number) always re-opens the confirm
}

function cancelFastConfirm() {
  fastDismissed.value = true
}

async function confirmFastFinalize() {
  const m = snapshot.value
  if (!m || fastLeftPick.value == null || fastRightPick.value == null || fastFinalizing.value) return
  fastFinalizing.value = true
  try {
    await finalizeGameFast(m.id, fastLeftPick.value, fastRightPick.value)
    // Picks reset via the currentGameIndex watcher once the new snapshot lands.
  } catch (e) {
    flashActionError(e instanceof ApiRequestError ? e.message : 'Could not record the result')
    fastDismissed.value = true // don't leave the confirm dialog stuck open over a failed call
  } finally {
    fastFinalizing.value = false
  }
}

// ---------------------------------------------------------------------------------------------
// Round-13: tournament context. When this match belongs to a tournament, the Live screen shows
// the tournament's upcoming games below the current match (and stops vertically centering the
// match — it centers within its own panel instead). After the match finishes, the result
// overlay shows "Next up…" with the next pairing dimmed until each player scans their badge
// (store/nextUp.ts); both scanned -> that slot's match auto-starts.
const tournament = ref<TournamentDetail | null>(null)
let tournamentForMatchId: number | null = null

async function loadTournamentContext(m: Match) {
  if (m.tournamentId == null) {
    tournament.value = null
    return
  }
  if (m.id === tournamentForMatchId && tournament.value) return
  tournamentForMatchId = m.id
  try {
    tournament.value = await getTournament(m.tournamentId)
  } catch {
    tournament.value = null
  }
}

function pendingSlots(t: TournamentDetail): TournamentBracketSlot[] {
  const all = [...t.groupRounds.flat(), ...t.rounds.flat()]
  return all.filter((sl) => sl.winnerPlayerId == null && !sl.isBye && sl.id !== snapshot.value?.tournamentSlotId)
}

// Shown under the live match: the next few pairings that have no result yet (TBD ones included
// so the shape of what's coming is visible).
const upcomingSlots = computed(() => {
  const t = tournament.value
  if (!t || snapshot.value?.tournamentId == null) return []
  return pendingSlots(t).slice(0, 4)
})

// The next STARTABLE pairing (both players resolved, no match yet) — the "Next up…" candidate.
function nextStartableSlot(t: TournamentDetail): TournamentBracketSlot | null {
  return pendingSlots(t).find((sl) => sl.matchId == null && sl.playerA && sl.playerB) ?? null
}

const startingNext = ref(false)

async function maybeOfferNextUp() {
  const m = snapshot.value
  if (!m || m.tournamentId == null || m.status !== 'finished') return
  try {
    const t = await getTournament(m.tournamentId)
    tournament.value = t
    const slot = nextStartableSlot(t)
    if (slot && slot.playerA && slot.playerB) {
      setNextUp(slot.id, slot.playerA, slot.playerB)
      // Hold the result overlay open for check-ins instead of auto-returning home.
      if (resultTimer) clearTimeout(resultTimer)
    }
  } catch {
    /* no tournament context — overlay behaves as before */
  }
}

watch(
  () => nextUp.scanned,
  async () => {
    if (!nextUpReady() || startingNext.value || nextUp.slotId == null || !nextUp.left || !nextUp.right) return
    startingNext.value = true
    try {
      const match = await createMatch({
        // Format fields are overridden server-side with the TOURNAMENT's format (round-13);
        // these just satisfy the payload shape.
        matchTypeCode: snapshot.value?.matchType.code ?? 'rapid11',
        bestOf: snapshot.value?.bestOf ?? 3,
        leftPlayerId: nextUp.left.id,
        rightPlayerId: nextUp.right.id,
        firstServer: 'left',
        table: tableStore.table,
        tournamentSlotId: nextUp.slotId,
        // Round-31 (2026-08-31) — carry the device's Fast mode setting into the auto-started
        // next tournament pairing too, same as the New match screen does.
        fastMode: settings.fastMode,
      })
      clearNextUp()
      showResult.value = false
      navigate('live', { matchId: match.id })
    } catch (e) {
      flashActionError(e instanceof ApiRequestError ? e.message : 'Could not start the next match')
      clearNextUp()
    } finally {
      startingNext.value = false
    }
  },
)


// ---------------------------------------------------------------------------------------------
// Round-15 (2026-08-31): voice scoring. kmb-stt's classified commands (src/stt.ts) drive the
// live score through the SAME postPoint/undo REST calls as touch — never local state. All
// interpretation is delegated to src/voiceScore.ts (pure): scores only move forward (5-2 can
// never become 1-1), a reversed reading ("5-2" heard when the table is 2-5) is auto-resolved
// when only ONE orientation is legal, and an anchor pins the first spoken number to a side —
// either a COLOR word ("5-2 rojo": left card is the RED corner, right is BLUE, like boxing) or
// the SPEAKER ("5-2 gano yo"/"punto mío"): the utterance's x-vector is cosine-matched against
// the two players\' enrolled voice profiles (GET /api/voice/profiles, cached per match).
// Every command shows a popup chip + its own chime (sounds.voice / voiceReject) so the room
// always hears whether the table understood.
const voicePopup = ref<{ text: string; ok: boolean } | null>(null)
let voicePopupTimer: ReturnType<typeof setTimeout> | null = null

function showVoicePopup(text: string, ok: boolean) {
  voicePopup.value = { text, ok }
  if (ok) sounds.voice()
  else sounds.voiceReject()
  if (voicePopupTimer) clearTimeout(voicePopupTimer)
  voicePopupTimer = setTimeout(() => (voicePopup.value = null), 3500)
}

let voiceProfiles: VoiceProfile[] | null = null
async function loadVoiceProfiles(): Promise<VoiceProfile[]> {
  if (voiceProfiles) return voiceProfiles
  try {
    voiceProfiles = (await getVoiceProfiles()).items.filter((pr) => pr.centroid && pr.centroid.length > 0)
  } catch {
    voiceProfiles = []
  }
  return voiceProfiles
}

// Round-28 (2026-08-31): candidate-scope ladder (services/candidateScope.ts, DESIGN.md's
// candidate-scope section). During a LIVE MATCH, speaker matching for BOTH the score/win-claim
// anchor ("gano yo") and the point-claim ("punto mío"/"punto tuyo") must only ever consider the
// match's own two players — never the full enrolled roster — so a bystander's voice can never
// outscore an actual match player just because more people happen to be enrolled elsewhere.
// Replaces the old hand-rolled per-side cosine loop with the SAME shared ranking/banding
// machinery cornerClaim.ts uses (rankSpeakerCandidates + classifyConfidenceBand) against the
// match-scoped candidate list. This also means point-claim now gets the 'dubious' ask-to-confirm
// band (previously binary accept/reject) — see handleVoiceCommand's point_claim branch and
// startPointClaimConfirmation below. Anchor resolution stays 'high'-only, unchanged from before
// this refactor (asking "who said that" mid-score-call is out of scope for this feature).
interface MatchSpeakerClassification {
  bandResult: ConfidenceBandResult
  scopeStr: string
  durationSec: number | null | undefined
  /** Physical side of the TOP candidate, or null when there's no comparable candidate at all
   *  (unenrolled players, no fingerprint, or an spk-model mismatch). */
  side: Side | null
}

async function classifySpeakerAgainstMatch(cmd: SttCommandEvent): Promise<MatchSpeakerClassification | null> {
  const m = snapshot.value
  const fp = spkForCommand(cmd)
  if (!m || !fp) return null
  const profiles = await loadVoiceProfiles()
  const scope = matchScope([m.currentPlayers.left.id, m.currentPlayers.right.id])
  const candidates = scopedCandidates(
    profiles.map((pr) => ({ playerId: pr.playerId, centroid: pr.centroid ?? null, spkModelVersion: pr.spkModelVersion })),
    scope,
  )
  const scopeStr = scopeLabel(scope, candidates.length)
  const ranked = rankSpeakerCandidates(fp.spk, fp.spkModelVersion, candidates)
  let bandResult = classifyConfidenceBand(ranked, fp.durationSec)
  // Round-28 (unenrolled players — item 4): the match scope nominally has TWO players but only
  // ONE has a voice profile — a single-candidate softmax can never express "or maybe it's the
  // unenrolled one", so auto-applying here would silently assume the enrolled player is always
  // the one speaking. Downgrade 'high' -> 'dubious' (still asks, naming the one enrolled
  // candidate) rather than auto-apply OR discard real signal. When NEITHER player is enrolled,
  // `ranked` is empty and the band is already 'low' — nothing to downgrade, no ask, no apply
  // (the safer of the two options named in the requirement: matching literally cannot tell two
  // unenrolled voices apart, so the table stays silent rather than guessing).
  if (scope.kind === 'match' && ranked.length === 1 && bandResult.band === 'high') {
    bandResult = { ...bandResult, band: 'dubious' }
  }
  const top = bandResult.top
  const side: Side | null = top ? (top.id === m.currentPlayers.left.id ? 'left' : 'right') : null
  // Round-22 (2026-08-31): "track everything" (DESIGN.md § Observability) — every anchor/point
  // speaker-match attempt ships to Loki, including the reject case, so debugging works from the
  // eventstream. Compact fields only. Outcome naming matches cornerClaim.ts's for continuity.
  const trackAttempt = (outcome: string, extra: Record<string, unknown> = {}) =>
    shipEvent('speaker-side-match', { raw: cmd.raw, lang: cmd.lang, claimant: cmd.claimant, outcome, durationSec: fp.durationSec, scope: scopeStr, ...extra })
  if (!top) {
    trackAttempt('no_candidate_above_floor')
  } else {
    const candidateSummary = ranked.map((r) => ({ side: r.id === m.currentPlayers.left.id ? 'left' : 'right', cos: Math.round(r.similarity * 1000) / 1000 }))
    const outcome = bandResult.band === 'high' ? 'accepted' : bandResult.band === 'dubious' ? 'needs_confirmation' : 'below_confidence_threshold'
    trackAttempt(outcome, {
      candidates: candidateSummary,
      chosenSide: side,
      confidence: Math.round(top.confidence * 1000) / 1000,
      margin: bandResult.margin != null ? Math.round(bandResult.margin * 1000) / 1000 : null,
    })
  }
  return { bandResult, scopeStr, durationSec: fp.durationSec, side }
}

function matchPct(similarity: number): string {
  return `${Math.round(similarity * 100)}%`
}

function otherSide(side: Side): Side {
  return side === 'left' ? 'right' : 'left'
}

async function postVoicePoints(addLeft: number, addRight: number) {
  const m = snapshot.value
  if (!m) return
  posting.value = true
  try {
    for (let i = 0; i < addLeft; i++) await postPoint(m.id, 'left', 'voice')
    for (let i = 0; i < addRight; i++) await postPoint(m.id, 'right', 'voice')
  } finally {
    posting.value = false
  }
}

// Round-28 (2026-08-31): the 'dubious' band for an in-match point-claim — a clear-winner speaker
// match that doesn't clear the auto-accept bar (or a single-enrolled-player match where the OTHER
// player is unenrolled, see classifySpeakerAgainstMatch's downgrade above). Same ask/answer state
// machine as corner-claim confirmation (services/confirmFlow.ts, "¿Eres Luuk?" via TTS +
// ConfirmClaimPrompt.vue) — reused as-is: the question is "are you the SPEAKER", identical
// semantics whether the claim was a corner claim or a point claim, so no new prompt/TTS text is
// needed. Only 'confirmed' applies anything; the point side is derived from `cmd.claimant`
// relative to the CONFIRMED candidate's CURRENT side (re-read from the live snapshot at resolve
// time, not the side captured when the ask started — a game-ending end-switch could happen during
// the ~10s decision window).
function startPointClaimConfirmation(cmd: SttCommandEvent, cls: MatchSpeakerClassification): void {
  const top = cls.bandResult.top
  const side = cls.side
  const m = snapshot.value
  if (!top || !side || !m) return
  const matchedPlayer = m.currentPlayers[side]
  const color: 'red' | 'blue' = side === 'left' ? 'red' : 'blue'
  const runnerUp = cls.bandResult.runnerUp
  const result: CornerClaimResult = {
    outcome: 'needs_confirmation',
    side,
    color,
    player: matchedPlayer,
    other: null,
    confidencePct: Math.round(top.confidence * 100),
    message: '',
    confirm: {
      candidateId: top.id,
      rawCosine: Math.round(top.similarity * 1000) / 1000,
      runnerUp: runnerUp
        ? { id: runnerUp.id, cosine: Math.round(runnerUp.similarity * 1000) / 1000, confidencePct: Math.round(runnerUp.confidence * 100) }
        : null,
      marginPct: Math.round((cls.bandResult.margin ?? 0) * 100),
      durationSec: cls.durationSec ?? null,
    },
    scopeLabel: cls.scopeStr,
  }
  startConfirmation({
    cmd,
    screen: 'live',
    result,
    scope: cls.scopeStr,
    command: 'point_claim',
    onResolve: (resolution, resolvedPlayer) => {
      if (resolution === 'denied') {
        showVoicePopup(`Not ${matchedPlayer.displayName} — point not recorded`, false)
        return
      }
      if (resolution !== 'confirmed') return // timeout/cancelled: silent, per the standing design.
      const cur = snapshot.value
      const matchedSide: Side | null =
        cur && cur.currentPlayers.left.id === resolvedPlayer.id ? 'left' : cur && cur.currentPlayers.right.id === resolvedPlayer.id ? 'right' : null
      if (!cur || cur.status !== 'live' || !matchedSide) {
        showVoicePopup(`${resolvedPlayer.displayName} confirmed, but the match changed — point not recorded`, false)
        return
      }
      const pointSide = cmd.claimant === 'me' ? matchedSide : otherSide(matchedSide)
      showVoicePopup(`Point ${pointSide === 'left' ? 'red' : 'blue'} (${cur.currentPlayers[pointSide].displayName}) (confirmed)`, true)
      void postVoicePoints(pointSide === 'left' ? 1 : 0, pointSide === 'right' ? 1 : 0).catch((e) => {
        flashActionError(e instanceof ApiRequestError ? e.message : 'Could not record the point')
      })
    },
  })
}

async function handleVoiceCommand(cmd: SttCommandEvent) {
  const m = snapshot.value
  if (!m || m.status !== 'live' || showResult.value || posting.value) return
  // Round-31 (2026-08-31): fast mode has no running per-point score to interpret voice
  // score-calling against (`game.scores` is always the 0-0 placeholder) — score/point-claim
  // voice commands are meaningless here by construction, so they're silently ignored rather
  // than misfiring off a permanently-0 baseline.
  if (m.fastMode) return
  const game = m.currentGameIndex != null ? m.games[m.currentGameIndex] : null
  if (!game) return

  // Round-16: match % of the speaker-identified anchor, surfaced in the popup when one was
  // used — "run training then see a live match % estimate" (user request, item 7).
  let anchorMatchPct: string | null = null

  // The anchor for "which side does the FIRST spoken number belong to": a color word wins
  // (red = left corner, blue = right), else the identified speaker ("gano yo" → their side,
  // "ganas tú" → the other side). Round-28: 'high'-band only, unchanged from before the
  // candidate-scope refactor — see classifySpeakerAgainstMatch's docblock.
  async function resolveAnchor(): Promise<Side | undefined> {
    if (cmd.color) return cmd.color === 'red' ? 'left' : 'right'
    if (cmd.claimant) {
      const cls = await classifySpeakerAgainstMatch(cmd)
      if (cls?.side && cls.bandResult.band === 'high' && cls.bandResult.top) {
        anchorMatchPct = matchPct(cls.bandResult.top.confidence)
        return cmd.claimant === 'me' ? cls.side : otherSide(cls.side)
      }
    }
    return undefined
  }

  if (cmd.command === 'score' || (cmd.command === 'win_claim' && cmd.score)) {
    const heard: [number, number] = [cmd.score!.left, cmd.score!.right]
    const anchor = await resolveAnchor()
    const res = interpretScore({ left: game.scores.left, right: game.scores.right }, heard, anchor)
    const pctSuffix = anchorMatchPct ? ` (voice match ${anchorMatchPct})` : ''
    if (res.action === 'apply') {
      showVoicePopup(`Heard ${heard[0]}–${heard[1]} → ${res.left}–${res.right}${pctSuffix}`, true)
      try {
        await postVoicePoints(res.addLeft, res.addRight)
      } catch (e) {
        flashActionError(e instanceof ApiRequestError ? e.message : 'Could not record voice points')
      }
    } else if (res.action === 'ambiguous') {
      showVoicePopup(`Heard ${heard[0]}–${heard[1]} — say it with "rojo"/"azul" to pick a side`, false)
    } else {
      const why = res.reason === 'backwards' ? 'scores can\u2019t go backwards' : res.reason === 'too_far' ? 'too far from the current score' : 'no change'
      showVoicePopup(`Heard ${heard[0]}–${heard[1]} — ignored (${why})`, false)
    }
    return
  }

  // Round-28: "punto mío"/"punto tuyo" ("my point"/"your point") — claimant-relative, so it
  // depends on knowing WHO spoke. 'high' band applies immediately (unchanged); 'dubious' now
  // starts the same ask/answer confirmation flow corner-claim uses (previously this band wasn't
  // even reachable here — point_claim was strictly binary); 'low' (or no comparable candidate at
  // all — unenrolled players) does nothing but tell the room the claim needs a color word or
  // enrolled voices, per the standing false-negative-biased design.
  if (cmd.command === 'point_claim') {
    let side: Side | null = cmd.color ? (cmd.color === 'red' ? 'left' : 'right') : null
    let pointMatchPct: string | null = null
    if (!side && cmd.claimant) {
      const cls = await classifySpeakerAgainstMatch(cmd)
      if (cls?.side && cls.bandResult.top) {
        if (cls.bandResult.band === 'high') {
          pointMatchPct = matchPct(cls.bandResult.top.confidence)
          side = cmd.claimant === 'me' ? cls.side : otherSide(cls.side)
        } else if (cls.bandResult.band === 'dubious') {
          startPointClaimConfirmation(cmd, cls)
          return
        }
        // 'low': side stays null, falls through to the message below.
      }
    }
    if (!side) {
      showVoicePopup('Heard a point claim — say "punto rojo"/"punto azul" or train your voice', false)
      return
    }
    const pctSuffix = pointMatchPct ? ` (voice match ${pointMatchPct})` : ''
    showVoicePopup(`Point ${side === 'left' ? 'red' : 'blue'} (${m.currentPlayers[side].displayName})${pctSuffix}`, true)
    try {
      await postVoicePoints(side === 'left' ? 1 : 0, side === 'right' ? 1 : 0)
    } catch (e) {
      flashActionError(e instanceof ApiRequestError ? e.message : 'Could not record the point')
    }
    return
  }

  if (cmd.command === 'win_claim') {
    // No score attached — nothing verifiable to apply; acknowledge so the claimer knows the
    // table heard them, but the score itself must come from a score command or taps.
    showVoicePopup('Heard a win claim — say the score too ("11-7 gano yo")', false)
  }
}

let unsubVoice: (() => void) | null = null
onMounted(() => {
  unsubVoice = onSttCommand((cmd) => void handleVoiceCommand(cmd))
})

// User feedback items 1 + 2 — in-card rating/head-to-head: each player's rating IN THIS MATCH'S
// POOL (rapid/classical have separate ratings), and the head-to-head split, both sides oriented
// to the CURRENT physical left/right (unlike the players' identity, which stays stable across
// end switches for the head-to-head fetch key above).
function footerRating(player: Match['currentPlayers']['left']): string {
  const pool = snapshot.value?.matchType.ratingPool ?? 'rapid'
  const r = player.ratings[pool]
  return r.provisional ? `${r.rating}?` : `${r.rating}`
}


</script>

<template>
  <div class="flex-1 min-h-0 flex flex-col relative overflow-y-auto">
    <div v-if="!snapshot && !showResult" class="flex-1 flex items-center justify-center text-neutral-500 text-lg">
      {{ tableStore.status === 'connected' ? 'No live match' : 'Connecting…' }}
    </div>

    <template v-else-if="snapshot">
      <!-- Round-5 feedback item 17: Abandon moved into the top bar's right slot as an X icon
           (KomboLogo.vue generalized into a reusable left/right-icon component this round). Same
           confirm flow as before — this just opens the dialog. -->
      <KomboLogo />

      <p v-if="actionError" class="text-center text-red-400 font-semibold text-sm shrink-0">{{ actionError }}</p>

      <!-- Round-15: voice-command popup — every classified utterance gets visible+audible
           feedback here, applied or not, so the room knows what the table understood. -->
      <transition name="fade">
        <div v-if="voicePopup" class="fixed top-14 inset-x-0 z-40 flex justify-center pointer-events-none">
          <div
            class="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold shadow-xl border"
            :class="voicePopup.ok ? 'bg-emerald-950/95 border-emerald-600 text-emerald-200' : 'bg-neutral-900/95 border-amber-600 text-amber-300'"
          >
            <span class="text-base">🎙</span>
            <span>{{ voicePopup.text }}</span>
          </div>
        </div>
      </transition>

      <!-- Round-5 feedback item 16: cards + score bug + stats now live in one flex-1 column,
           vertically centered in whatever room is left below the bar/Undo row (was stacked from
           the top). Item 14: the decorative TableDrawing table above the cards is gone. -->
      <div class="flex-1 flex flex-col gap-2 min-h-0" :class="upcomingSlots.length > 0 ? 'justify-start' : 'justify-center'">
        <!-- Round-3 feedback item 4: the server indicator is now a glowing ring around the
             avatar (ServeIndicator.vue), not a ball icon beside it. Item 18: pt-5 (was py-3) adds
             breathing room above the avatar so it isn't flush against the card's top edge. -->
        <!-- Round-5 feedback item 15: score bug — round-9 (2026-08-31) moved it ABOVE the cards ("best of /
             standing should be above, not below the game") — and switched from a games-won number to DOTS — `gamesToWin` dots per side, filled as that
             side wins games. Still a subtle, TV-broadcast-style pill. -->
        <div class="flex justify-center pb-1 shrink-0">
          <div class="flex items-center gap-3 rounded-full bg-neutral-900 border border-neutral-800 px-4 py-1.5">
            <span class="flex items-center gap-1">
              <span
                v-for="i in gamesToWin"
                :key="`l${i}`"
                class="w-2.5 h-2.5 rounded-full"
                :class="i <= leftGamesWon ? 'bg-emerald-400' : 'bg-neutral-700'"
              ></span>
            </span>
            <span class="text-[10px] font-bold text-neutral-400 uppercase tracking-wide px-2 py-0.5 rounded-full bg-neutral-800">
              Bo{{ snapshot.bestOf }}
            </span>
            <span class="flex items-center gap-1">
              <span
                v-for="i in gamesToWin"
                :key="`r${i}`"
                class="w-2.5 h-2.5 rounded-full"
                :class="i <= rightGamesWon ? 'bg-emerald-400' : 'bg-neutral-700'"
              ></span>
            </span>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-2 px-2 shrink-0">
          <!-- Round-15: boxing-style corner colors — left is always the RED corner, right the
               BLUE one ("5-2 rojo" → red side has 5), so voice never needs player names.
               Round-17 (live-testing feedback, rider 3): bumped up from "kept subtle" — a
               thicker top edge, a full tinted border, and a faintly red-washed background —
               still dark-minimal (a step up, not a filled color block). Round-25: this box chrome
               (border/tint/dot) now lives in the shared TableSide.vue — Home's TableDrawing uses
               the exact same component for its sides — instead of a second copy of these classes. -->
          <TableSide side="left" class="gap-1.5 pt-5 pb-3">
            <ServeIndicator :active="snapshot.server === 'left'">
              <Avatar :player="snapshot.currentPlayers.left" :size="76" />
            </ServeIndicator>
            <span class="text-lg font-semibold truncate max-w-[85%]">{{ snapshot.currentPlayers.left.displayName }}</span>
            <!-- Round-31 (2026-08-31, DESIGN.md §23) — fast mode REPLACES the +/- tap flow with
                 a 1..pointsToWin button grid (5 per row) picking this side's final score for the
                 current game, instead of adding a second control alongside it (would clutter a
                 card that has no running score to show anyway). -->
            <div v-if="!snapshot.fastMode" class="flex items-center gap-2.5">
              <button
                type="button"
                class="w-7 h-7 min-h-0 min-w-0 shrink-0 rounded-full bg-neutral-800/70 active:bg-neutral-700 flex items-center justify-center text-neutral-400 disabled:opacity-30"
                :disabled="!canMinus('left')"
                aria-label="Remove last point from left"
                @click="onUndo"
              >
                <svg viewBox="0 0 24 24" class="w-3.5 h-3.5" fill="currentColor"><path :d="mdiMinus" /></svg>
              </button>
              <div class="text-[min(5rem,10vw)] leading-none font-black tabular-nums min-w-[2ch] text-center">{{ currentGame?.scores.left ?? 0 }}</div>
              <button
                type="button"
                class="w-7 h-7 min-h-0 min-w-0 shrink-0 rounded-full bg-neutral-800/70 active:bg-neutral-700 flex items-center justify-center text-neutral-400 disabled:opacity-30"
                :disabled="snapshot.status !== 'live' || posting"
                aria-label="Add point to left"
                @click="onTapScore('left')"
              >
                <svg viewBox="0 0 24 24" class="w-3.5 h-3.5" fill="currentColor"><path :d="mdiPlus" /></svg>
              </button>
            </div>
            <div v-else class="grid grid-cols-5 gap-1 w-full max-w-[230px] px-1">
              <button
                v-for="n in fastNumbers"
                :key="n"
                type="button"
                class="tap-target rounded-lg text-sm font-bold flex items-center justify-center disabled:opacity-30"
                :class="fastLeftPick === n ? 'bg-emerald-600 text-white' : 'bg-neutral-800/70 text-neutral-300 active:bg-neutral-700'"
                :disabled="snapshot.status !== 'live' || fastFinalizing"
                :aria-label="`Set left score to ${n}`"
                @click="selectFastPick('left', n)"
              >
                {{ n }}
              </button>
            </div>
            <div class="mt-0.5 text-[11px] font-mono text-neutral-500 leading-tight">
              <span>{{ footerRating(snapshot.currentPlayers.left) }} elo</span>
            </div>
          </TableSide>

          <TableSide side="right" class="gap-1.5 pt-5 pb-3">
            <ServeIndicator :active="snapshot.server === 'right'">
              <Avatar :player="snapshot.currentPlayers.right" :size="76" />
            </ServeIndicator>
            <span class="text-lg font-semibold truncate max-w-[85%]">{{ snapshot.currentPlayers.right.displayName }}</span>
            <div v-if="!snapshot.fastMode" class="flex items-center gap-2.5">
              <button
                type="button"
                class="w-7 h-7 min-h-0 min-w-0 shrink-0 rounded-full bg-neutral-800/70 active:bg-neutral-700 flex items-center justify-center text-neutral-400 disabled:opacity-30"
                :disabled="!canMinus('right')"
                aria-label="Remove last point from right"
                @click="onUndo"
              >
                <svg viewBox="0 0 24 24" class="w-3.5 h-3.5" fill="currentColor"><path :d="mdiMinus" /></svg>
              </button>
              <div class="text-[min(5rem,10vw)] leading-none font-black tabular-nums min-w-[2ch] text-center">{{ currentGame?.scores.right ?? 0 }}</div>
              <button
                type="button"
                class="w-7 h-7 min-h-0 min-w-0 shrink-0 rounded-full bg-neutral-800/70 active:bg-neutral-700 flex items-center justify-center text-neutral-400 disabled:opacity-30"
                :disabled="snapshot.status !== 'live' || posting"
                aria-label="Add point to right"
                @click="onTapScore('right')"
              >
                <svg viewBox="0 0 24 24" class="w-3.5 h-3.5" fill="currentColor"><path :d="mdiPlus" /></svg>
              </button>
            </div>
            <div v-else class="grid grid-cols-5 gap-1 w-full max-w-[230px] px-1">
              <button
                v-for="n in fastNumbers"
                :key="n"
                type="button"
                class="tap-target rounded-lg text-sm font-bold flex items-center justify-center disabled:opacity-30"
                :class="fastRightPick === n ? 'bg-emerald-600 text-white' : 'bg-neutral-800/70 text-neutral-300 active:bg-neutral-700'"
                :disabled="snapshot.status !== 'live' || fastFinalizing"
                :aria-label="`Set right score to ${n}`"
                @click="selectFastPick('right', n)"
              >
                {{ n }}
              </button>
            </div>
            <div class="mt-0.5 text-[11px] font-mono text-neutral-500 leading-tight">
              <span>{{ footerRating(snapshot.currentPlayers.right) }} elo</span>
            </div>
          </TableSide>
        </div>

        <!-- Round-31 (2026-08-31, DESIGN.md §23) — fast mode: once both sides have a pick that
             ISN'T a legal result (win-by/points-to-win violated), say so inline instead of
             opening the confirm dialog below. Clears itself as soon as the picks form a valid
             pair (fastValid flips true) or either side is re-tapped. -->
        <p v-if="fastInvalidMessage" class="text-center text-amber-400 font-semibold text-sm px-4 shrink-0">{{ fastInvalidMessage }}</p>

        <!-- Round-7 feedback item 19: the stats/history block (matches played, win rates,
             recent results) that used to live here is REMOVED — its data now belongs on the
             /players profile screen instead. Only the format footer below survives. -->

        <!-- Round-13: tournament context — the next few pending pairings, shown under the
             current match (which stops being vertically centered when this panel is present). -->
        <div v-if="upcomingSlots.length > 0" class="px-2 shrink-0">
          <div class="text-[10px] font-bold uppercase tracking-wide text-neutral-600 mb-1">Coming up</div>
          <div class="flex gap-2 overflow-x-auto">
            <div
              v-for="sl in upcomingSlots"
              :key="sl.id"
              class="shrink-0 flex items-center gap-2 rounded-xl bg-neutral-900 border border-neutral-800/70 px-3 py-1.5"
            >
              <Avatar :player="sl.playerA" :size="22" />
              <span class="text-xs font-semibold text-neutral-400 max-w-[7rem] truncate">{{ sl.playerA?.displayName ?? 'TBD' }}</span>
              <span class="text-[10px] text-neutral-600">vs</span>
              <span class="text-xs font-semibold text-neutral-400 max-w-[7rem] truncate">{{ sl.playerB?.displayName ?? 'TBD' }}</span>
              <Avatar :player="sl.playerB" :size="22" />
            </div>
          </div>
        </div>

        <!-- Round-3 feedback item 10: match type name as a very subtle footer below the two
             halves (used to overflow a header row). -->
        <div class="text-center text-[11px] text-neutral-600 font-mono pt-2 shrink-0">{{ snapshot.matchType.name }}</div>
      </div>

      <!-- Round-31 (2026-08-31, DESIGN.md §23) — fast mode's one-tap confirm before a game (and
           possibly the match) is finalized. Opens automatically once both sides have a VALID
           pick; cancelling just dismisses it (picks stay put, still tappable) so a mistap never
           silently ends anything. Reused ConfirmDialog — same component Settings' Abandon
           confirm uses — per the "keep it fast, no multi-step dialog" rider requirement. -->
      <ConfirmDialog
        :open="fastConfirmOpen"
        :title="t('fastConfirmTitle')"
        :body="fastConfirmBody"
        :confirm-label="t('fastConfirmYes')"
        :cancel-label="t('fastConfirmNo')"
        @confirm="confirmFastFinalize"
        @cancel="cancelFastConfirm"
      />

      <!-- Round-10 (2026-08-31): "Switch ends" is a FULLSCREEN animated takeover, not a small
           banner — the two players' avatars slide past each other (mirroring the physical walk
           around the table) under a big pulsing title. Same 6s timer/show logic as before. -->
      <transition name="fade">
        <div v-if="showBanner" class="fixed inset-0 z-40 bg-neutral-950 flex flex-col items-center justify-center gap-10 overflow-hidden" @click="showBanner = false">
          <h1 class="text-5xl font-black text-amber-400 tracking-tight ends-title">Switch ends</h1>
          <div class="relative w-full max-w-xl h-24">
            <div class="absolute top-0 left-0 ends-slide-right flex flex-col items-center gap-1">
              <Avatar :player="snapshot.currentPlayers.left" :size="64" />
              <span class="text-xs font-semibold text-neutral-400">{{ snapshot.currentPlayers.left.displayName }}</span>
            </div>
            <div class="absolute top-0 right-0 ends-slide-left flex flex-col items-center gap-1">
              <Avatar :player="snapshot.currentPlayers.right" :size="64" />
              <span class="text-xs font-semibold text-neutral-400">{{ snapshot.currentPlayers.right.displayName }}</span>
            </div>
            <div class="absolute inset-x-0 top-1/2 -translate-y-1/2 border-t border-dashed border-neutral-800 -z-10"></div>
          </div>
          <p class="text-neutral-600 text-sm">Walk around the table — tap to dismiss</p>
        </div>
      </transition>
    </template>

    <!-- Round-10 (2026-08-31): winner's avatar highlighted (emerald ring); the GAME score
         (games won, e.g. 2–1) is the prominent number between the players; the per-game point
         scores are demoted to a small muted row under it. -->
    <div v-if="showResult && snapshot" class="fixed inset-0 z-50 bg-neutral-950 flex items-center justify-center p-8" @click="returnHome">
      <div class="flex flex-col items-center gap-6 max-w-[90%]">
        <h1 class="text-3xl font-black text-emerald-400">
          {{ snapshot.winnerPlayerId === snapshot.players.left.id ? snapshot.players.left.displayName : snapshot.players.right.displayName }} wins!
        </h1>
        <!-- Round-13: elo lines removed (smaller card), game score can never wrap. -->
        <div class="flex items-center gap-6">
          <div class="flex flex-col items-center gap-2">
            <span class="rounded-full" :class="snapshot.winnerPlayerId === snapshot.players.left.id ? 'ring-4 ring-emerald-400' : ''">
              <Avatar :player="snapshot.players.left" :size="64" />
            </span>
            <div class="font-semibold">{{ snapshot.players.left.displayName }}</div>
          </div>
          <div class="text-5xl font-black tabular-nums whitespace-nowrap shrink-0">
            {{ snapshot.gamesWon[snapshot.players.left.id] ?? 0 }}–{{ snapshot.gamesWon[snapshot.players.right.id] ?? 0 }}
          </div>
          <div class="flex flex-col items-center gap-2">
            <span class="rounded-full" :class="snapshot.winnerPlayerId === snapshot.players.right.id ? 'ring-4 ring-emerald-400' : ''">
              <Avatar :player="snapshot.players.right" :size="64" />
            </span>
            <div class="font-semibold">{{ snapshot.players.right.displayName }}</div>
          </div>
        </div>
        <div class="flex gap-3">
          <div v-for="g in snapshot.games" :key="g.index" class="rounded-lg bg-neutral-900 border border-neutral-800/60 px-2.5 py-1 text-xs font-mono text-neutral-500">
            {{ g.scores.left }}–{{ g.scores.right }}
          </div>
        </div>
        <!-- Round-13 "Next up…" (tournament matches): the next pairing, dimmed until each
             player scans their badge; both checked in -> the slot's match auto-starts. -->
        <div v-if="nextUp.slotId != null && nextUp.left && nextUp.right" class="flex flex-col items-center gap-3 rounded-2xl border border-neutral-800 bg-neutral-900 px-6 py-4" @click.stop>
          <div class="text-xs font-bold uppercase tracking-wide text-amber-400">Next up…</div>
          <div class="flex items-center gap-6">
            <div class="flex flex-col items-center gap-1 transition-opacity" :class="nextUp.scanned[nextUp.left.id] ? '' : 'opacity-40'">
              <span class="rounded-full" :class="nextUp.scanned[nextUp.left.id] ? 'ring-2 ring-emerald-400' : ''">
                <Avatar :player="nextUp.left" :size="48" />
              </span>
              <span class="text-sm font-semibold">{{ nextUp.left.displayName }}</span>
            </div>
            <span class="text-neutral-600 text-sm">vs</span>
            <div class="flex flex-col items-center gap-1 transition-opacity" :class="nextUp.scanned[nextUp.right.id] ? '' : 'opacity-40'">
              <span class="rounded-full" :class="nextUp.scanned[nextUp.right.id] ? 'ring-2 ring-emerald-400' : ''">
                <Avatar :player="nextUp.right" :size="48" />
              </span>
              <span class="text-sm font-semibold">{{ nextUp.right.displayName }}</span>
            </div>
          </div>
          <p class="text-neutral-500 text-xs">{{ startingNext ? 'Starting…' : 'Players, scan your badges to start' }}</p>
        </div>
        <p class="text-neutral-500 text-sm">Tap anywhere to continue</p>
      </div>
    </div>

  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Round-10 switch-ends takeover: the two avatars cross the screen in opposite directions
   (ease-in-out, slight vertical arc) while the title breathes. Pure CSS — no motion lib needed
   for two keyframe tracks. */
.ends-title {
  animation: ends-breathe 1.6s ease-in-out infinite;
}
@keyframes ends-breathe {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.06);
    opacity: 0.85;
  }
}
.ends-slide-right {
  animation: ends-cross-right 2.4s ease-in-out infinite;
}
.ends-slide-left {
  animation: ends-cross-left 2.4s ease-in-out infinite;
}
@keyframes ends-cross-right {
  0%,
  12% {
    left: 0;
    transform: translate(0, 0);
  }
  88%,
  100% {
    left: 100%;
    transform: translate(-100%, -14px);
  }
}
@keyframes ends-cross-left {
  0%,
  12% {
    right: 0;
    transform: translate(0, 0);
  }
  88%,
  100% {
    right: 100%;
    transform: translate(100%, 14px);
  }
}
</style>
